import { CoreTooltip } from '@featuring-corp/components';
import { IconInformationFilled } from '@featuring-corp/icons';
import { vars } from '@styles/vanilla-extract/theme.css';
import { flex } from '@styles/vanilla-extract/utils';
import { useTranslation } from 'react-i18next';
import React, { FunctionComponent, createElement } from 'react';

import CoreSkeletonBox from '@shared/CoreSkeletonBox';

import * as styles from './style.css';

export interface CardGroupItem {
	label?: string;
	subLabel?: string;
	tooltip?: string;
	icon?: FunctionComponent<{ fill?: string }>;
	content?: string;
	disabled?: {
		text: string;
		tooltip: string;
	};
}

export interface CardGroupSectionProps {
	cardGroup?: CardGroupItem[];
	isLoading?: boolean;
	isError?: boolean;
	lackOfPermission?: boolean;
}

const CardGroupSection = ({
	cardGroup,
	isLoading,
	isError,
	lackOfPermission,
}: CardGroupSectionProps) => {
	const { t } = useTranslation('commonInfReport');
	const available = !isLoading && !isError && !lackOfPermission;

	const getIconFill = (disabled?: boolean) =>
		disabled ? vars.semantic.color.icon.tertiary : vars.semantic.color.icon.primary;

	return (
		<div className={styles.container}>
			{cardGroup?.map((item, idx) => (
				<div key={idx} className={styles.card}>
					<div>
						<div className={flex({ gap: 'spacing-300' })}>
							<div className={styles.label}>{item.label}</div>
							{item.tooltip && (
								<CoreTooltip text={item.tooltip} zIndex={10000}>
									<IconInformationFilled fill={vars.semantic.color.icon.tertiary} />
								</CoreTooltip>
							)}
						</div>
						{item.subLabel && <div className={styles.subLabel}>{item.subLabel}</div>}
					</div>

					<div className={styles.content({ unavailable: lackOfPermission })}>
						{item.icon &&
							createElement(item.icon, { fill: getIconFill(Boolean(item.disabled)) })}

						{available && (
							<>
								{!item.disabled && (item.content ?? '-')}
								{item.disabled && (
									<CoreTooltip text={item.disabled.tooltip} autoAdjust>
										<div className={styles.disabled}>{item.disabled.text}</div>
									</CoreTooltip>
								)}
							</>
						)}
						{!lackOfPermission && isLoading && (
							<CoreSkeletonBox
								className={styles.skeleton}
								_width="auto"
								_height="24px"
								radius="4px"
								delay={`${idx * 0.4}s`}
							/>
						)}
						{!lackOfPermission && isError && '-'}
						{lackOfPermission && (
							<div>{t('features.report.card-group-section.lack-of-permission')}</div>
						)}
					</div>
				</div>
			))}
		</div>
	);
};

export default CardGroupSection;
